/**
 * my-hiisi-hydrate.js — T-108 / T-108-fix / T-110 cache-busting hydrate for
 * /pages/my-hiisi.
 *
 * Why this exists:
 *   Shopify storefront edge caches the rendered Liquid page per URL, and
 *   different edge nodes hold different snapshots. After a customer saves a
 *   fit_profile, approves a build, or sees Pole's open-revision land, a
 *   refresh can pull a stale snapshot from a different edge — the customer
 *   then sees their *previous* state. We hit /apps/hiisi-bespoke/customer/
 *   order on every page load with a fresh fetch (App Proxy → Worker → live
 *   Admin GraphQL) and patch the DOM in place so the stale Liquid render
 *   never lingers.
 *
 * T-110 (2026-05-16): all reloadFresh() calls removed. The T-108 design
 * fell back to `window.location.replace(url + '?_t=…')` whenever the
 * fetched state disagreed with the Liquid-rendered branch (terms accepted
 * vs pending, invoices state-machine flip, bike-card approval flip).
 * Storefront edge cache didn't reliably honour the `_t` cache-bust — the
 * same stale HTML came back, triggering another mismatch, another reload,
 * the customer saw the page strobe and any state-flip UI flicker. T-108-fix
 * capped the loop at one reload-per-session but didn't fix the underlying
 * staleness; reload-once with a stale return still leaves the UI wrong.
 *
 * The fix: patch the DOM in place. Snippets now render every branch up-
 * front with `hidden` attributes on the inactive ones (terms-widget,
 * invoices-widget); JS just flips `hidden` based on fresh data. The bike-
 * card / versions-timeline state machine is too large to fully re-render
 * in JS, so we do the narrow set of patches the brief calls out:
 *   1. Toggle the approve-form visibility based on `approval[lid]`.
 *   2. Update the "Pole's latest build" header when `current_proposal[lid]`
 *      advances to a new version id.
 *   3. Prepend a single "📩 Pole sent vN — refresh to view details" notice
 *      to the timeline when a brand-new Pole version has landed since the
 *      Liquid render. The customer's manual click triggers a fresh render
 *      (one event = one navigation, no loop possible) and the next page
 *      shows the full version card.
 *
 * Render contract:
 *   - Liquid is the SEO-safe + JS-off fallback. JS never gates content.
 *   - JS runs on DOMContentLoaded, makes ONE fetch per detail-view shown,
 *     and patches the DOM in place. No reloads.
 *   - Fetch failure (network / 401 / 404 / 5xx) is silent: Liquid render
 *     stays visible. Customer always sees *something*, even if it's a few
 *     minutes stale.
 *
 * Cost-leak (T-039 / T-103d):
 *   The /customer/order endpoint excludes hiisi_pole_internal namespace at
 *   the GraphQL query layer, the typed CustomerOrderResponse interface, and
 *   tests/cost-leak.test.ts. This file reads ONLY fields the endpoint
 *   surfaces: profiles, terms, invoice_scalars, invoice1, approval,
 *   versions, current_proposal. No sourcing / cost / margin metadata is
 *   touched.
 *
 * Single-fetch orchestration:
 *   One page-level fetch dispatched to per-snippet apply functions. No
 *   snippet fetches independently; no fetch fires twice per page.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Resolve the visible detail-view's order id. The section renders one
   * `[data-bmh-view="detail"]` per Hiisi order, hidden by default; the
   * router script in sections/bespoke-my-hiisi.liquid un-hides the one
   * matching `?order=<id|order_number>` from the query string. We hydrate
   * only the visible detail — no point fetching for hidden ones.
   */
  function resolveActiveDetail() {
    var nodes = document.querySelectorAll('[data-bmh-view="detail"]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].hidden) return nodes[i];
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------------------
  function formatMeasure(value, unit) {
    if (value == null || value === '') return '';
    return value + ' ' + unit;
  }

  function formatDateLong(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_e) {
      return '';
    }
  }

  // ---------------------------------------------------------------------------
  // applyConsultation — locked-cards + form prefill + edit-payload JSON +
  // T-125 widget-state resolve (measurements-saved + consultation-done flips)
  // ---------------------------------------------------------------------------
  // Per-bike fit profile lives in `profiles[lid]`. Each locked-card carries
  // `data-bmh-fit-locked` + `data-li-id` + a set of `data-bmh-locked-key="…"`
  // dd elements (one per profile field). Hydrate walks every locked-card,
  // looks up its lid, and overwrites each dd's content from the fetched
  // profile. Locked-card visibility flips in place via the `hidden`
  // attribute (no DOM construction) — Liquid pre-renders one card per
  // line_item even when no profile exists yet, so the card slot is always
  // available for hydrate to populate.
  //
  // T-125 (2026-05-20) — clone the consultation-widget Liquid state machine
  // into a small client-side resolver. Two cache-stale scenarios we now fix
  // (mirroring the T-107c pill resolver pattern):
  //
  //   (a) Measurements saved → ready_to_book. Customer just saved fit
  //       measurements; the widget's own JS already flipped the wraps in
  //       the same tab. After a reload that pulls a stale edge-cached
  //       Liquid render, the page renders with form visible again. The
  //       resolver below sees profiles[lid].height_cm for every line item,
  //       hides the form-wrap, shows the summary-wrap, and updates
  //       data-cw-state="ready_to_book".
  //
  //   (b) Staff marked consultation done → "Consultation completed {date}".
  //       Liquid renders the booking CTA in summary-wrap; fresh data shows
  //       consultations[<lid>].done_at (or consult_at is now in the past).
  //       Resolver flips summary-substate from "book" → "done" and patches
  //       the displayed date.
  //
  // Out of scope: flipping from the gate or booked branches into the
  // combined branch — those are separate <section> trees with no shared
  // form-wrap/summary-wrap markup. Their cache-stale window is much
  // narrower (gate flips only on customer-tag change; booked flips when
  // consult_at crosses now), so a reload-once on next click is acceptable.
  // 2026-08-30 — resolve the order-level question switches from
  // /customer/order's `customer_steps`. Absent field / absent key => OFF, the
  // same rule the Worker and the Liquid snippets apply. Only an explicit
  // `true` switches a surface on. Kaannetty 2026-08-30: oletus oli aiemmin
  // painvastainen, jolloin jokainen tilaus kysyi mitat ja tarjosi
  // konsultaatiota ilman etta kukaan oli niin paattanyt.
  function resolveCustomerSteps(raw) {
    var steps = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    return {
      fitProfile: steps.fit_profile === true,
      consultation: steps.consultation === true,
    };
  }

  function applyConsultation(profiles, consultations, consultAt, customerSteps) {
    profiles = profiles && typeof profiles === 'object' ? profiles : {};
    consultations = consultations && typeof consultations === 'object' ? consultations : {};
    var steps = resolveCustomerSteps(customerSteps);

    // ---- Switched-off surfaces win over whatever Liquid rendered ----
    // Pole can flip a switch while the customer has the page open, or the
    // edge can serve a snapshot from before the flip. Either way the page can
    // still be showing a form the Worker now refuses (409 fit_profile_disabled)
    // or a booking CTA Pole has withdrawn. Hide those first, and skip the
    // hydration work that only makes sense for a surface that is still live.
    //
    // This block only ever HIDES. A surface Liquid chose not to render stays
    // absent — hydrate never resurrects one.
    var stepsWidget = document.querySelector('[data-cw-state]');
    if (stepsWidget) {
      stepsWidget.setAttribute('data-cw-fit-enabled', steps.fitProfile ? 'true' : 'false');
      stepsWidget.setAttribute('data-cw-consult-enabled', steps.consultation ? 'true' : 'false');
      if (!steps.fitProfile && !steps.consultation) {
        stepsWidget.setAttribute('hidden', '');
        return;
      }
      if (!steps.fitProfile) {
        var offFormWrap = stepsWidget.querySelector('[data-cw-form-wrap]');
        if (offFormWrap) offFormWrap.setAttribute('hidden', '');
        var offCards = stepsWidget.querySelector('[data-cw-locked-cards]');
        if (offCards) offCards.setAttribute('hidden', '');
      }
      if (!steps.consultation) {
        var offSummary = stepsWidget.querySelector('[data-cw-summary-wrap]');
        if (offSummary) offSummary.setAttribute('hidden', '');
        var offCalendar = stepsWidget.querySelector('[data-bmh-calendar-mount]');
        if (offCalendar) offCalendar.setAttribute('hidden', '');
      }
    }

    // Measurements aren't asked for any more — don't repopulate or un-hide
    // the locked cards from fresh data. The saved profiles themselves stay
    // in the metafield and stay visible to staff; this is about the page.
    if (!steps.fitProfile) return;

    document.querySelectorAll('[data-bmh-fit-locked]').forEach(function (card) {
      var lid = card.getAttribute('data-li-id');
      if (!lid) return;
      var prof = profiles[lid];

      // T-110: card visibility flips entirely in place — the Liquid template
      // always emits a `[data-bmh-fit-locked]` element per line_item with
      // `hidden` set when no profile existed at render time. If hydrate
      // shows a profile now exists (or the previous profile got cleared
      // server-side), we flip the `hidden` attribute. No DOM construction
      // needed — the locked-card markup is permanent in the page.
      var hasProfile = !!(prof && prof.height_cm);
      if (hasProfile) {
        card.removeAttribute('hidden');
      } else {
        card.setAttribute('hidden', '');
        return;
      }

      var setKey = function (key, html) {
        var dd = card.querySelector('[data-bmh-locked-key="' + key + '"]');
        if (dd) dd.innerHTML = html;
      };

      setKey('height_cm', prof.height_cm ? '<span class="hiisi-measure" data-value="' + prof.height_cm + '" data-unit="cm">' + formatMeasure(prof.height_cm, 'cm') + '</span>' : '');
      setKey('inseam_cm', prof.inseam_cm ? '<span class="hiisi-measure" data-value="' + prof.inseam_cm + '" data-unit="cm">' + formatMeasure(prof.inseam_cm, 'cm') + '</span>' : '');
      setKey('weight_kg', prof.weight_kg ? '<span class="hiisi-measure" data-value="' + prof.weight_kg + '" data-unit="kg">' + formatMeasure(prof.weight_kg, 'kg') + '</span>' : '');
      setKey('wingspan_cm', prof.wingspan_cm ? '<span class="hiisi-measure" data-value="' + prof.wingspan_cm + '" data-unit="cm">' + formatMeasure(prof.wingspan_cm, 'cm') + '</span>' : '');

      var postureLabel = prof.posture ? (prof.posture.charAt(0).toUpperCase() + prof.posture.slice(1)) : '';
      setKey('posture', postureLabel);

      var descentLabels = { 1: 'Easy', 2: 'Moderate', 3: 'Strong', 4: 'Hard', 5: 'Race pace' };
      var descentN = parseInt(prof.descent_aggression, 10);
      if (descentN >= 1 && descentN <= 5) {
        setKey('descent_aggression', descentN + ' — ' + descentLabels[descentN]);
      } else {
        setKey('descent_aggression', '');
      }

      var savedAtSpan = card.querySelector('[data-bmh-locked-key="saved_at"]');
      if (savedAtSpan) savedAtSpan.textContent = formatDateLong(prof.saved_at);

      // Edit-payload JSON next to the locked card. The consultation-widget's
      // own JS reads this on Edit click to prefill the form. Stringifying
      // the live profile keeps Edit accurate even when the locked-card text
      // was already hydrated.
      var payloadEl = card.querySelector('[data-bmh-fit-payload]');
      if (payloadEl) {
        try { payloadEl.textContent = JSON.stringify(prof); } catch (_e) { /* ignore */ }
      }
    });

    // Primary-lid form prefill: never touched. The form may be open mid-edit
    // and overwriting input.value would discard whatever the user is typing.
    // The Edit-click path re-reads the (now-hydrated) fit-payload JSON, so
    // a stale form-default never bites in practice.

    // -----------------------------------------------------------------------
    // T-125 — widget-state resolve.
    // -----------------------------------------------------------------------
    // Only the combined section (measurements_pending / ready_to_book) carries
    // the form-wrap + summary-wrap pair we can patch. The gate and booked
    // branches render different DOM trees — querySelector returns null on
    // their `[data-cw-form-wrap]` and the function bails out cleanly.
    var widget = document.querySelector('[data-cw-state]');
    if (!widget) return;
    var renderedState = widget.getAttribute('data-cw-state') || '';
    if (renderedState !== 'measurements_pending' && renderedState !== 'ready_to_book') {
      // gate / booked branches — out of scope, see fn header.
      return;
    }

    var formWrap = widget.querySelector('[data-cw-form-wrap]');
    var summaryWrap = widget.querySelector('[data-cw-summary-wrap]');
    if (!formWrap || !summaryWrap) return;

    // ---- Measurements-saved → ready_to_book ----
    // Mirror the Liquid cw_unsaved_count loop: a line item counts as "saved"
    // when profiles[lid].height_cm is truthy (same gate as the widget's own
    // submit-success path). We need the rendered DOM's line-item ids — the
    // locked-card list is the canonical source (Liquid emits one card per
    // line item, always).
    var lockedCards = widget.querySelectorAll('[data-bmh-fit-locked]');
    var allSaved = lockedCards.length > 0;
    lockedCards.forEach(function (card) {
      var lid = card.getAttribute('data-li-id') || '';
      var prof = profiles[lid];
      if (!prof || !prof.height_cm) allSaved = false;
    });

    if (allSaved && renderedState === 'measurements_pending') {
      formWrap.setAttribute('hidden', '');
      summaryWrap.removeAttribute('hidden');
      widget.setAttribute('data-cw-state', 'ready_to_book');
      renderedState = 'ready_to_book';
    }

    // ---- Consultation done → "Consultation completed {date}" substate ----
    // cw_has_prior_done semantic (Liquid: lines 79–108):
    //   - ANY consultations[<lid>].done_at is set, OR
    //   - consultation_at is set AND in the past.
    // We compute both signals client-side; whichever fires first wins.
    var latestDoneAt = '';
    Object.keys(consultations).forEach(function (lid) {
      var entry = consultations[lid];
      if (entry && typeof entry === 'object') {
        var doneAt = entry.done_at;
        if (typeof doneAt === 'string' && doneAt && doneAt > latestDoneAt) {
          latestDoneAt = doneAt;
        }
      }
    });
    var priorDone = !!latestDoneAt;
    if (!priorDone && typeof consultAt === 'string' && consultAt) {
      var consultUnix = Date.parse(consultAt);
      if (!isNaN(consultUnix) && consultUnix < Date.now()) {
        priorDone = true;
        // Fall back to consult_at for the displayed timestamp when no
        // per-bike done_at was stamped — same fallback the Liquid render
        // uses for the cw_latest_done_at slot.
        latestDoneAt = consultAt;
      }
    }

    // Substate flip only meaningful when summary-wrap is itself visible.
    // In measurements_pending the form is the focus and the summary-wrap
    // stays hidden, so a prior_done in that state doesn't surface anywhere
    // until the form completes (which is fine — same as Liquid's behaviour).
    if (renderedState === 'ready_to_book') {
      var bookSubstate = summaryWrap.querySelector('[data-cw-summary-substate="book"]');
      var doneSubstate = summaryWrap.querySelector('[data-cw-summary-substate="done"]');
      if (bookSubstate && doneSubstate) {
        if (priorDone) {
          bookSubstate.setAttribute('hidden', '');
          doneSubstate.removeAttribute('hidden');
          // Format like the Liquid `%B %-d, %Y` filter (e.g. "May 15, 2026")
          // so a hydrate flip reads the same as a fresh server render.
          var displayEl = doneSubstate.querySelector('[data-cw-done-at-display]');
          if (displayEl && latestDoneAt) {
            displayEl.textContent = formatApprovedPillDate(latestDoneAt);
          }
        } else {
          bookSubstate.removeAttribute('hidden');
          doneSubstate.setAttribute('hidden', '');
        }
      }
      widget.setAttribute('data-cw-prior-done', priorDone ? 'true' : 'false');
      if (priorDone && latestDoneAt) {
        widget.setAttribute('data-cw-latest-done-at', latestDoneAt);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // applyTerms — branch toggle + in-place text patches
  // ---------------------------------------------------------------------------
  // T-110: terms-widget.liquid now renders BOTH the accepted-state and
  // pending-state sections always, with `hidden` on the inactive one. JS
  // flips the `hidden` attribute based on fresh `terms` from /customer/order.
  // No section ever needs to be constructed in JS, no reload required.
  //
  // DOM mutations:
  //   - `[data-bmh-terms-state="accepted"]` hidden ↔ visible
  //   - `[data-bmh-terms-state="pending"]` hidden ↔ visible
  //   - inside accepted-state, `[data-bmh-terms-accepted-date]` text content
  //   - inside accepted-state, `[data-bmh-terms-accepted-version]` text content
  function applyTerms(terms) {
    var acceptedSection = document.querySelector('[data-bmh-terms-state="accepted"]');
    var pendingSection = document.querySelector('[data-bmh-terms-state="pending"]');
    if (!acceptedSection && !pendingSection) return;

    var freshlyAccepted = !!(terms && terms.accepted_at);

    if (freshlyAccepted) {
      if (acceptedSection) acceptedSection.removeAttribute('hidden');
      if (pendingSection) pendingSection.setAttribute('hidden', '');

      var dateEl = acceptedSection
        ? acceptedSection.querySelector('[data-bmh-terms-accepted-date]')
        : null;
      if (dateEl) dateEl.textContent = formatDateLong(terms.accepted_at);

      var versionEl = acceptedSection
        ? acceptedSection.querySelector('[data-bmh-terms-accepted-version]')
        : null;
      if (versionEl && terms.version) versionEl.textContent = terms.version;
    } else {
      // Server-side `tw_any_awaiting_approval` (= "any bike has a Pole-sent
      // version and no approval yet") is what gates the pending-section's
      // visibility. We don't recompute that from the fetched data here —
      // if the Liquid render decided the pending section should NOT exist
      // (e.g. no Hiisi versions yet), the section won't be in the DOM.
      // The accepted-section is the only one we forcibly hide in the
      // not-yet-accepted branch.
      if (acceptedSection) acceptedSection.setAttribute('hidden', '');
    }
  }

  // ---------------------------------------------------------------------------
  // applyInvoices — branch toggle + in-place text patches
  // ---------------------------------------------------------------------------
  // T-110: invoices-widget.liquid now renders ALL SIX state-machine branches
  // always, with `hidden` on the five inactive ones. JS flips visibility
  // based on the fresh scalars and populates per-slot text in the active
  // branch.
  //
  // State priority chain mirrors the Liquid `if / elsif` order:
  //   both_paid > invoice2_sent > invoice1_paid > invoice1_sent >
  //   all_approved > pending
  //
  // DOM mutations:
  //   - `[data-bmh-invoices-state="<key>"]` hidden flip across all 6 sections
  //   - inside the active section, `[data-bmh-invoice-slot="<key>"]` text
  //     patches for invoice1_sent_at / invoice1_paid_at / invoice2_sent_at /
  //     invoice2_paid_at (money amounts are server-rendered and stable per
  //     order — JS does not re-format them).
  function computeInvoiceStateFromScalars(scalars, fallbackAllApproved) {
    if (!scalars) return 'pending';
    if (scalars.invoice2_paid_at) return 'both_paid';
    if (scalars.invoice2_sent_at) return 'invoice2_sent';
    if (scalars.invoice1_paid_at) return 'invoice1_paid';
    if (scalars.invoice1_sent_at) return 'invoice1_sent';
    // `all_approved` vs `pending` isn't computable from invoice scalars alone
    // — we trust the Liquid-rendered branch to be correct for this leaf
    // distinction (it's driven by `approval[lid]` which the customer can
    // also flip, but that path triggers a full /customer/approve POST and
    // its own page reload anyway).
    return fallbackAllApproved ? 'all_approved' : 'pending';
  }

  function applyInvoices(scalars, approvalMap) {
    var sections = document.querySelectorAll('[data-bmh-invoices-state]');
    if (sections.length === 0) return;

    // Detect the Liquid-rendered "active" branch so we can preserve the
    // all_approved vs pending distinction when the fetched scalars don't
    // resolve to a invoice1/2-sent state. The pre-T-110 widget renders
    // exactly one section visible; post-T-110 it renders all six with five
    // hidden, but the same Liquid logic still decides which one starts
    // visible.
    var renderedActive = null;
    sections.forEach(function (s) {
      if (!s.hasAttribute('hidden')) renderedActive = s.getAttribute('data-bmh-invoices-state');
    });
    var renderedAllApproved = renderedActive === 'all_approved' || renderedActive === 'invoice1_sent' ||
      renderedActive === 'invoice1_paid' || renderedActive === 'invoice2_sent' || renderedActive === 'both_paid';

    // If we know approval[lid] from the fetch we can compute approved-ness
    // exactly. Otherwise fall back to the rendered branch.
    var freshAllApproved = renderedAllApproved;
    if (approvalMap && typeof approvalMap === 'object') {
      // any approval entry with approved_at counts as "at least one approved"
      // — but the strict gate is "ALL approved". Without the line_items list
      // we can't compute "all" strictly, so use a permissive check that
      // matches what the Liquid render does when scalars aren't yet set.
      // The next page-load (after a real approve POST) re-renders correctly.
      var anyApproved = false;
      Object.keys(approvalMap).forEach(function (k) {
        if (approvalMap[k] && approvalMap[k].approved_at) anyApproved = true;
      });
      // If the rendered branch was 'pending' but fresh data shows an approval
      // exists, advance to 'all_approved'. Otherwise stick with rendered.
      if (renderedActive === 'pending' && anyApproved) freshAllApproved = true;
    }

    var target = computeInvoiceStateFromScalars(scalars, freshAllApproved);

    sections.forEach(function (section) {
      var key = section.getAttribute('data-bmh-invoices-state');
      if (key === target) {
        section.removeAttribute('hidden');
      } else {
        section.setAttribute('hidden', '');
      }
    });

    // Patch the date / money slots inside the (now-active) section.
    var activeSection = document.querySelector('[data-bmh-invoices-state="' + target + '"]');
    if (!activeSection || !scalars) return;

    var slot = function (key, value) {
      activeSection.querySelectorAll('[data-bmh-invoice-slot="' + key + '"]').forEach(function (el) {
        if (value != null && value !== '') el.textContent = value;
      });
    };

    slot('invoice1_sent_at', formatDateLong(scalars.invoice1_sent_at));
    slot('invoice1_paid_at', formatDateLong(scalars.invoice1_paid_at));
    slot('invoice2_sent_at', formatDateLong(scalars.invoice2_sent_at));
    slot('invoice2_paid_at', formatDateLong(scalars.invoice2_paid_at));
  }

  // ---------------------------------------------------------------------------
  // applyBikeCards — approve-form toggle + new-version notice
  // ---------------------------------------------------------------------------
  // T-110: bike-card's full state machine is 10 branches across 1500 Liquid
  // lines — too brittle to fully re-render in JS. We do the narrow set of
  // patches the T-110 brief calls out:
  //
  //   1. Toggle approve-form visibility based on approval[lid].
  //      If approval[lid] is set post-fetch but the form is still visible
  //      (Liquid rendered the pre-approval state), hide it. We do NOT
  //      construct an approve form when approval got cleared (open-revision
  //      case) — that requires the version's full pricing context which we
  //      don't have client-side. The customer sees the existing "approved"
  //      banner go away on the next page-load.
  //
  //   2. Detect a new Pole version (current_proposal[lid] points at a
  //      version id newer than what's rendered). Prepend a single
  //      `<aside class="mht-fresh-update-notice">` to the timeline with
  //      a "Refresh to view full details" CTA. The customer clicks once,
  //      gets a fresh server render, sees the full new version card.
  //      We don't try to render the new version card client-side — that
  //      would replicate ~150 lines of Liquid (spec dl + comments thread +
  //      approve form + pricing breakdown).
  //
  // DOM mutations:
  //   - `[data-mht-approve-actions]` hidden flip per approval state
  //   - prepend `<aside data-mht-fresh-update-notice>` to `[data-mht-timeline]`
  //   - update `[data-bmh-bike-fingerprint]` attribute on the bike-card
  //     article so downstream listeners (if any) see the fresh state
  // T-107b — Liquid-parity date formatters for hydrate-injected text. Two
  // pieces of approval-banner / pill copy reference the rendered date format
  // exactly, so a fresh page-load and a hydrate-patched DOM read the same way.
  //
  //   formatDateDmy        — Liquid `%-d %b %Y`     → "18 May 2026"
  //     Used in: acknowledged / invoice_sent banner sub-states.
  //
  //   formatApprovedPillDate — Liquid `%B %-d, %Y`  → "May 18, 2026"
  //     Used in: status-pill text post-approve ("Approved May 18, 2026").
  //
  // formatDateLong (above) uses {month: 'short'} → "May 18, 2026". Same as
  // pill-date for May/Jun/Jul (3-letter months) but DIVERGES for full names
  // like September. We use the dedicated formatters here so the DOM patch
  // matches the next server-render byte-for-byte.
  function formatDateDmy(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      var month = d.toLocaleDateString('en-US', { month: 'short' });
      return d.getDate() + ' ' + month + ' ' + d.getFullYear();
    } catch (_e) {
      return '';
    }
  }

  function formatApprovedPillDate(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      var month = d.toLocaleDateString('en-US', { month: 'long' });
      return month + ' ' + d.getDate() + ', ' + d.getFullYear();
    } catch (_e) {
      return '';
    }
  }

  // T-107d — bike-card pill resolver: complete bc_state ladder.
  //
  // Mirrors snippets/my-hiisi-bike-card.liquid:269–529 (the bc_state +
  // bc_stage_pill_text case) for EVERY state a customer can see, including
  // the 7-rung production ladder + shipped + the approved-gate invoice_sent
  // variant that T-107c initially deferred. The resolver returns null only
  // when it lacks data to decide (e.g. production state without a known
  // stage — defensive); the caller treats null as "leave Liquid as-is".
  //
  // Inputs (`env` is built once per applyBikeCards call):
  //   approval, versions, drafts, currentProposal, production, invoice1
  //     — same maps the hydrate fetch returns
  //   allApprovedFlag, allInvoice1PaidFlag
  //     — order-level cross-bike aggregates needed for the production gate
  //   consultationState: 'past' | 'future' | 'unset'
  //     — derived once from the rendered journey-stepper (see
  //       resolveConsultationState below); per-order, not per-bike
  //
  // T-107b approval-state default behaviour PRESERVED: when approval[lid]
  // is set, the production gate is closed, AND invoice1 hasn't been sent,
  // we still emit the green-muted "Approved {date}" pill that T-107b
  // shipped. T-107d only adds the invoice_sent variant on top.
  function resolveBikeCardPill(env, lid) {
    var approval = env.approval || {};
    var versions = env.versions || {};
    var drafts = env.drafts || {};
    var production = env.production || {};
    var invoice1 = env.invoice1 || {};

    // ── Shipped — highest priority. ─────────────────────────────────────
    var prod = production[lid];
    if (prod && prod.shipped_at) {
      var shippedDateStr = formatApprovedPillDate(prod.shipped_at);
      if (!shippedDateStr) return null;
      return { text: 'Shipped ' + shippedDateStr, className: 'bmh-pill--green' };
    }

    // ── in_production: gate open (all approved + all invoice1 paid). ────
    // Mirror bc_customer_stage mapping from production[lid].stage. Since the
    // 2026-07-28 acknowledgement rework each internal id names the work it
    // confirms FINISHED, so it reads one step later than its name:
    //   '' / unknown     → 'queued'
    //   'sourcing'       → 'components'   (nothing acknowledged yet)
    //   'cnc_queued'     → 'sourced'      (components have arrived)
    //   'cnc_done'       → 'machined'
    //   'finishing'      → 'ecoat'        (legacy, no longer written)
    //   'assembly'       → 'coated'       (e-coat finished)
    //   'qc'             → 'assembled'
    //   'packaging'      → 'checked'      (QC passed)
    //   'ready_to_ship'  → 'packed'
    // The customer_stage values map to the pill text+class table in
    // bike-card.liquid (the `in_production` case in bc_stage_pill_text).
    if (env.allApprovedFlag && env.allInvoice1PaidFlag) {
      var rawStage = (prod && prod.stage) || '';
      var customerStage = mapProductionStageToCustomerStage(rawStage);
      return pillForCustomerStage(customerStage);
    }

    // ── Approval state (T-107b green-muted default + T-107d invoice_sent
    //    variant). Mirror bc_gate_variant: when invoice1[lid].sent_at is
    //    present (and not stale wrt approved_at), pill goes gold "Approved,
    //    awaiting Invoice 1". Every other gate variant collapses to the
    //    default green-muted "Approved {date}".
    var approvalEntry = approval[lid];
    if (approvalEntry && approvalEntry.approved_at) {
      var dateStr = formatApprovedPillDate(approvalEntry.approved_at);
      if (!dateStr) return null;
      var inv = invoice1[lid];
      if (inv && inv.sent_at && env.allApprovedFlag && !isInvoice1StampStale(inv.sent_at, approvalEntry.approved_at)) {
        return { text: 'Approved, awaiting Invoice 1', className: 'bmh-pill--gold' };
      }
      return { text: 'Approved ' + dateStr, className: 'bmh-pill--green-muted' };
    }

    // ── proposal_ready branch. ──────────────────────────────────────────
    // A Pole-sent version exists → bc_state == 'proposal_ready' in Liquid.
    // Sub-state 'is_awaiting': latest customer version is newer than the
    // latest Pole-sent version (Pole owes a response). We derive this from
    // versions chronology rather than proposal_status[lid] so we don't need
    // /customer/order to surface the metafield — the signal is identical to
    // the customer-pending-counter detection further down in applyBikeCards.
    var versionsForLid = versions[lid] || [];
    var latestPoleSentAt = '';
    var hasLatestPole = false;
    for (var i = versionsForLid.length - 1; i >= 0; i--) {
      var v = versionsForLid[i];
      if (v && v.type === 'pole' && v.status === 'sent') {
        hasLatestPole = true;
        latestPoleSentAt = v.created_at || '';
        break;
      }
    }
    if (hasLatestPole) {
      var isAwaiting = false;
      for (var j = versionsForLid.length - 1; j >= 0; j--) {
        var c = versionsForLid[j];
        if (!c || c.type !== 'customer') continue;
        var cCreatedAt = c.created_at || '';
        if (latestPoleSentAt && cCreatedAt && latestPoleSentAt > cCreatedAt) break;
        isAwaiting = true;
        break;
      }
      if (isAwaiting) {
        return { text: "Awaiting Pole's response", className: 'bmh-pill--gold' };
      }
      return { text: 'Proposal ready', className: 'bmh-pill--gold' };
    }

    // ── bc_draft_config branch: consultation-aware. ─────────────────────
    // Liquid splits on consult_at:
    //   - consult_at < now  → 'spec_in_preparation' (neutral)
    //   - consult_at > now  → 'consultation_booked' (neutral)
    //   - no consult_at     → 'configured_pending' (neutral)
    // We derive consultationState from the journey-stepper render (step 3)
    // because /customer/order doesn't surface consultation_at as a scalar.
    // The journey state IS Liquid-rendered (potentially stale), but
    // consultation timestamps rarely drift in the edge-cache window — the
    // booking flow is out-of-band (Google Calendar) and customer-side
    // mutations are rare. Acceptable accuracy for a hydrate patch.
    var draftEntry = drafts[lid];
    if (draftEntry && draftEntry.spec) {
      if (env.consultationState === 'past') {
        return { text: 'Spec in preparation', className: 'bmh-pill--neutral' };
      }
      if (env.consultationState === 'future') {
        return { text: 'Consultation booked', className: 'bmh-pill--neutral' };
      }
      return { text: 'Configured · awaiting booking', className: 'bmh-pill--neutral' };
    }

    // ── default → awaiting_config. ──────────────────────────────────────
    return { text: 'Awaiting configuration', className: 'bmh-pill--gold' };
  }

  // T-107d — map raw production.stage (staff-written enum) to the
  // customer-facing taxonomy. Mirrors the bc_customer_stage case in
  // bike-card.liquid. Unknown / empty stage = 'queued' (post-invoice1-paid,
  // before anything has been acknowledged).
  function mapProductionStageToCustomerStage(rawStage) {
    switch (rawStage) {
      case 'sourcing':       return 'components';
      case 'cnc_queued':     return 'sourced';
      case 'cnc_done':       return 'machined';
      case 'finishing':      return 'ecoat';
      case 'assembly':       return 'coated';
      case 'qc':             return 'assembled';
      case 'packaging':      return 'checked';
      case 'ready_to_ship':  return 'packed';
      default:               return 'queued';
    }
  }

  // T-107d — pill text + class per customer_stage. Mirrors the
  // `in_production` case in bc_stage_pill_text (bike-card.liquid). Only
  // queued + components stay gold — nothing has been finished yet and the
  // customer is waiting. From the first acknowledgement on the pill goes
  // neutral: motion, no customer action needed.
  function pillForCustomerStage(stage) {
    switch (stage) {
      case 'queued':     return { text: 'Queued for production',   className: 'bmh-pill--gold' };
      case 'components': return { text: 'Components on order',     className: 'bmh-pill--gold' };
      case 'sourced':    return { text: 'Components sourced',      className: 'bmh-pill--neutral' };
      case 'machined':   return { text: 'Frame machined',          className: 'bmh-pill--neutral' };
      case 'ecoat':      return { text: 'E-coating under way',     className: 'bmh-pill--neutral' };
      case 'coated':     return { text: 'Assembly started',        className: 'bmh-pill--neutral' };
      case 'assembled':  return { text: 'Assembled',               className: 'bmh-pill--neutral' };
      case 'checked':    return { text: 'Quality check passed',    className: 'bmh-pill--neutral' };
      case 'packed':     return { text: 'Packed — ready to ship',  className: 'bmh-pill--neutral' };
      default:           return { text: 'Queued for production',   className: 'bmh-pill--gold' };
    }
  }

  // T-107d — Liquid bc_invoice1 stale-stamp guard. Mirrors the T-100 logic
  // in bike-card.liquid:220–232: when invoice1[lid] has a sent_at /
  // paid_at / ack_at older than the current approved_at, it's a leftover
  // from a prior round (Pole opens a revision but doesn't clear
  // invoice1[lid]); ignore it. Used by the resolver's approved-gate
  // invoice_sent check and by computeOrderProductionFlags' paid_at gate.
  function isInvoice1StampStale(stampIso, approvedAtIso) {
    if (!stampIso || !approvedAtIso) return false;
    var stampUnix = Date.parse(stampIso) || 0;
    var approvedUnix = Date.parse(approvedAtIso) || 0;
    if (stampUnix === 0 || approvedUnix === 0) return false;
    return stampUnix < approvedUnix;
  }

  // T-127b — derive order-level consultation state from Worker /customer/order
  // data, not the rendered Liquid DOM. Pre-T-127b this helper read
  // [data-bmh-journey-step="3"] attribute+text, which meant a stale edge-cached
  // Liquid snapshot pinned the resolved state to whatever was true at SSR-time
  // (kts. proposals/handout-T-127-state-resolve-juurisyy.md §Juuri 2).
  //
  // Semantics:
  //   - ANY consultations[<lid>].done_at set      → 'past'  (staff Done overrides booked)
  //   - consultationAt is a parseable past ISO    → 'past'
  //   - consultationAt is a parseable future ISO  → 'future'
  //   - otherwise                                  → 'unset'
  //
  // Returns 'past' | 'future' | 'unset'. Called once per applyBikeCards
  // invocation rather than per bike — consultation_at is order-level, and the
  // consultations map's "any done_at" aggregate is also order-scoped.
  function resolveConsultationState(consultations, consultationAt) {
    var anyDone = false;
    if (consultations && typeof consultations === 'object') {
      var keys = Object.keys(consultations);
      for (var i = 0; i < keys.length; i++) {
        var entry = consultations[keys[i]];
        if (entry && typeof entry === 'object' && typeof entry.done_at === 'string' && entry.done_at) {
          anyDone = true;
          break;
        }
      }
    }
    if (anyDone) return 'past';
    if (typeof consultationAt === 'string' && consultationAt) {
      var unix = Date.parse(consultationAt);
      if (!isNaN(unix)) {
        if (unix < Date.now()) return 'past';
        if (unix > Date.now()) return 'future';
      }
    }
    return 'unset';
  }

  // T-107c — order-level cross-bike production-gate flags. Mirrors Liquid's
  // bc_all_approved + bc_all_invoice1_paid aggregates. When both are true
  // the order has crossed the production gate and the pill belongs to the
  // Liquid-rendered production ladder (out of scope here).
  //
  // line_item_ids needed because we want "ALL of the order's bikes" — a
  // single approved bike out of three is NOT all_approved. We pass the
  // rendered DOM's line-item ids from the per-card forEach since /customer/
  // order doesn't return a flat lid list (it returns maps keyed by lid).
  function computeOrderProductionFlags(allLineItemIds, approval, invoice1) {
    if (!allLineItemIds || allLineItemIds.length === 0) {
      return { allApprovedFlag: false, allInvoice1PaidFlag: false };
    }
    var allApproved = true;
    var allInvoice1Paid = true;
    for (var k = 0; k < allLineItemIds.length; k++) {
      var iLid = allLineItemIds[k];
      var ap = approval && approval[iLid];
      if (!ap || !ap.approved_at) allApproved = false;
      var inv = invoice1 && invoice1[iLid];
      var paidAt = inv && inv.paid_at;
      // Liquid's T-100 guard drops invoice1 timestamps older than the
      // current approved_at (open_revision leaves stale stamps). Mirror it
      // so a round-1 paid_at doesn't false-positive the gate after Pole
      // opens round 2.
      if (paidAt && ap && ap.approved_at) {
        var paidUnix = Date.parse(paidAt) || 0;
        var approvedUnix = Date.parse(ap.approved_at) || 0;
        if (approvedUnix > 0 && paidUnix > 0 && paidUnix < approvedUnix) {
          paidAt = null;
        }
      }
      if (!paidAt) allInvoice1Paid = false;
    }
    return { allApprovedFlag: allApproved, allInvoice1PaidFlag: allInvoice1Paid };
  }

  // T-107b — approval-banner substate resolver, mirrors the per-mille / cascade
  // in snippets/my-hiisi-versions-timeline.liquid:206–252 (T-097 + T-100
  // logic). Priority: invoice_paid > invoice_sent > acknowledged > sent. Each
  // invoice1[lid] timestamp must be ≥ approval.approved_at (T-100 guard
  // against stale prior-round stamps after an open_revision). Acknowledged
  // also falls back to legacy approval.acknowledged.at / .acknowledged_at
  // shapes for orders predating the invoice1[lid] write path.
  function resolveApprovalSubstate(approvalEntry, invoice1Entry) {
    if (!approvalEntry || !approvalEntry.approved_at) return null;
    var approvedUnix = Date.parse(approvalEntry.approved_at) || 0;
    function isCurrentRound(iso) {
      if (!iso) return false;
      var unix = Date.parse(iso);
      if (!unix) return false;
      if (!approvedUnix) return true;
      return unix >= approvedUnix;
    }
    var inv = invoice1Entry || {};
    if (isCurrentRound(inv.paid_at)) return { substate: 'invoice_paid', sentAt: inv.sent_at, paidAt: inv.paid_at };
    if (isCurrentRound(inv.sent_at)) return { substate: 'invoice_sent', sentAt: inv.sent_at };
    var ackAt = '';
    if (isCurrentRound(inv.ack_at)) ackAt = inv.ack_at;
    else if (approvalEntry.acknowledged && isCurrentRound(approvalEntry.acknowledged.at)) ackAt = approvalEntry.acknowledged.at;
    else if (isCurrentRound(approvalEntry.acknowledged_at)) ackAt = approvalEntry.acknowledged_at;
    if (ackAt) return { substate: 'acknowledged', ackAt: ackAt };
    return { substate: 'sent' };
  }

  // 2026-09-15 — kertamaksu. Banneri kirjoitti aina toisen laskun takaisin
  // vaikka palvelin oli jo jattanyt sen pois, koska hydraatio ei tiennyt
  // maksutavasta mitaan. Arvo luetaan ensin sivun omasta atribuutista (se on
  // paikalla heti) ja paivitetaan /customer/orderin vastauksesta.
  var hiisiSinglePayment = false;
  function readSinglePaymentFromDom() {
    try {
      var grid = document.querySelector('[data-bmh-os-single-payment]');
      if (grid) hiisiSinglePayment = grid.getAttribute('data-bmh-os-single-payment') === 'true';
    } catch (_e) { /* ignore */ }
  }

  // T-107b — approval-banner copy. Mirrors versions-timeline.liquid
  // byte-for-byte so a hydrate-injected banner reads the same as a fresh
  // server-render of the same state.
  function buildApprovalBannerCopy(info, approvalEntry) {
    var versionId = (approvalEntry && approvalEntry.version_id) || '';
    if (info.substate === 'invoice_paid') {
      if (hiisiSinglePayment) {
        return {
          strongText: '✓ Paid in full — production starts',
          pText: 'Thanks! Your build is in the queue. Nothing further is due.',
        };
      }
      return {
        strongText: '✓ Invoice 1 paid — production starts',
        pText: 'Thanks! Your build is in the queue. Invoice 2 (final 50%) will arrive when production is complete.',
      };
    }
    if (info.substate === 'invoice_sent') {
      return {
        strongText: hiisiSinglePayment
          ? '💳 Your invoice is ready — pay to start production'
          : '💳 Invoice 1 is ready — pay to start production',
        pText: 'Sent ' + formatDateDmy(info.sentAt) + ' to your email. Once paid, production starts immediately. See the Invoices section above for the full breakdown.',
      };
    }
    if (info.substate === 'acknowledged') {
      return {
        strongText: '🔒 Pole has acknowledged your approval · ' + versionId + ' is your build',
        pText: 'Acknowledged on ' + formatDateDmy(info.ackAt) + ' — production starts within 2 business days. Invoice 1 will arrive in your email shortly.',
      };
    }
    // Default: 'sent' (approved, awaiting Pole confirmation).
    return {
      strongText: '✓ You approved ' + versionId + ' on ' + formatDateDmy(approvalEntry.approved_at),
      pText: 'Waiting for Pole to confirm — usually within 1 business day. After that, this spec is locked and Invoice 1 is on the way.',
    };
  }

  // T-107b — ensure the approval banner is in DOM and reflects the current
  // substate. Idempotent: if Liquid already rendered the right substate, this
  // is a no-op. If Liquid rendered pre-approval (no banner), we construct one.
  // If Liquid rendered an older substate (e.g. 'sent') and Pole has since
  // acknowledged, we flip the class + copy in place.
  var APPROVAL_BANNER_SUBSTATE_CLASSES = [
    'mht-approval-banner--sent',
    'mht-approval-banner--acknowledged',
    'mht-approval-banner--invoice_sent',
    'mht-approval-banner--invoice_paid',
  ];
  function ensureApprovalBanner(timeline, approvalEntry, invoice1Entry) {
    if (!timeline || !approvalEntry || !approvalEntry.approved_at) return;
    var info = resolveApprovalSubstate(approvalEntry, invoice1Entry);
    if (!info) return;
    var copy = buildApprovalBannerCopy(info, approvalEntry);
    var banner = timeline.querySelector('[data-mht-approval-banner]');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'mht-banner mht-banner-success mht-approval-banner mht-approval-banner--' + info.substate;
      banner.setAttribute('role', 'status');
      banner.setAttribute('data-mht-approval-banner', '');
      banner.setAttribute('data-mht-approval-substate', info.substate);
      var s = document.createElement('strong');
      s.textContent = copy.strongText;
      var p = document.createElement('p');
      p.textContent = copy.pText;
      banner.appendChild(s);
      banner.appendChild(p);
      timeline.insertBefore(banner, timeline.firstChild);
      return;
    }
    // Banner exists — sync if substate drifted (e.g. Liquid rendered 'sent'
    // pre-ack, but the fresh invoice1[lid].ack_at advances us to 'acknowledged').
    var renderedSubstate = banner.getAttribute('data-mht-approval-substate') || '';
    if (renderedSubstate === info.substate) return;
    APPROVAL_BANNER_SUBSTATE_CLASSES.forEach(function (cls) { banner.classList.remove(cls); });
    banner.classList.add('mht-approval-banner--' + info.substate);
    banner.setAttribute('data-mht-approval-substate', info.substate);
    var strongEl = banner.querySelector('strong');
    if (strongEl) strongEl.textContent = copy.strongText;
    var pEl = banner.querySelector('p');
    if (pEl) pEl.textContent = copy.pText;
  }

  // T-107b — bike-card title resolver, mirroring bike-card.liquid:588's
  // priority chain post-name-resolve-approval-haara: draft.spec.name (customer
  // inline-rename wins, T-118 invariant) → approval version's spec.name
  // (T-107b approval-frozen branch) → current_proposal version's spec.name →
  // server-stamped default ("Bike N" / "Frameset N", read from
  // data-bmh-rename-default).
  function resolveBikeCardTitle(lid, drafts, approval, currentProposal, versionsForLid, defaultTitle) {
    var draftName = '';
    var dEntry = drafts && drafts[lid];
    if (dEntry && dEntry.spec && typeof dEntry.spec.name === 'string') {
      draftName = dEntry.spec.name.trim();
    }
    if (draftName) return draftName;

    var approvedVersionId = (approval && approval[lid] && approval[lid].version_id) || '';
    if (approvedVersionId) {
      for (var i = 0; i < versionsForLid.length; i++) {
        var v = versionsForLid[i];
        if (v && v.id === approvedVersionId && v.spec && typeof v.spec.name === 'string') {
          var n = v.spec.name.trim();
          if (n) return n;
          break;
        }
      }
    }
    var proposedVersionId = (currentProposal && currentProposal[lid]) || '';
    if (proposedVersionId) {
      for (var j = 0; j < versionsForLid.length; j++) {
        var vp = versionsForLid[j];
        if (vp && vp.id === proposedVersionId && vp.spec && typeof vp.spec.name === 'string') {
          var pname = vp.spec.name.trim();
          if (pname) return pname;
          break;
        }
      }
    }
    return defaultTitle || '';
  }

  /** Short relative-time string for the customer-pending-counter banner.
   *  Format goal: "(2 hours ago)", "(yesterday)", "(3 days ago)". Used by
   *  applyBikeCards' T-111 pending-counter notice. */
  function formatRelativeTimeSince(iso) {
    if (!iso) return '';
    try {
      var then = new Date(iso).getTime();
      if (isNaN(then)) return '';
      var diffMs = Date.now() - then;
      if (diffMs < 0) return '(just now)';
      var minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return '(just now)';
      if (minutes < 60) return '(' + minutes + ' minute' + (minutes === 1 ? '' : 's') + ' ago)';
      var hours = Math.floor(minutes / 60);
      if (hours < 24) return '(' + hours + ' hour' + (hours === 1 ? '' : 's') + ' ago)';
      var days = Math.floor(hours / 24);
      if (days === 1) return '(yesterday)';
      if (days < 30) return '(' + days + ' days ago)';
      var months = Math.floor(days / 30);
      return '(' + months + ' month' + (months === 1 ? '' : 's') + ' ago)';
    } catch (_e) {
      return '';
    }
  }

  function applyBikeCards(versions, approval, currentProposal, drafts, invoice1, production, consultations, consultationAt) {
    versions = versions || {};
    approval = approval || {};
    currentProposal = currentProposal || {};
    drafts = drafts || {};
    invoice1 = invoice1 || {};
    production = production || {};
    consultations = consultations && typeof consultations === 'object' ? consultations : {};

    // T-107c — order-level constants shared across all bikes in this
    // applyBikeCards run. Computed once before the per-card loop:
    //   - consultationState: derived from Worker /customer/order data
    //     (T-127b — no longer reads stale Liquid DOM, kts. resolveConsultationState)
    //   - allApprovedFlag / allInvoice1PaidFlag: cross-bike production gate
    //
    // line-item ids come from the rendered DOM (the bike-card forEach
    // target). We collect them up front so computeOrderProductionFlags
    // can iterate every bike in the order, not just the one we're rendering.
    var consultationState = resolveConsultationState(consultations, consultationAt);
    var cardNodes = document.querySelectorAll('[data-bmh-bike-fingerprint]');
    var allLineItemIds = [];
    cardNodes.forEach(function (c) {
      var clid = c.getAttribute('data-li-id');
      if (clid) allLineItemIds.push(clid);
    });
    var gateFlags = computeOrderProductionFlags(allLineItemIds, approval, invoice1);
    var pillEnv = {
      approval: approval,
      versions: versions,
      drafts: drafts,
      currentProposal: currentProposal,
      production: production,
      invoice1: invoice1,
      consultationState: consultationState,
      allApprovedFlag: gateFlags.allApprovedFlag,
      allInvoice1PaidFlag: gateFlags.allInvoice1PaidFlag,
    };

    cardNodes.forEach(function (card) {
      var lid = card.getAttribute('data-li-id');
      if (!lid) return;

      var approvedAt = (approval[lid] && approval[lid].approved_at) || '';
      var freshCurrentId = currentProposal[lid] || '';

      // Compute the rendered fingerprint vs. the fresh one. Format:
      // `<approved_at>|<latest_pole_sent_id>`. If they match, nothing to do
      // for the approve-form toggle (Liquid render is consistent).
      var versionsForLid = versions[lid] || [];
      var latestPoleId = '';
      var latestPoleCreatedAt = '';
      for (var i = versionsForLid.length - 1; i >= 0; i--) {
        var v = versionsForLid[i];
        if (v && v.type === 'pole' && v.status === 'sent') {
          latestPoleId = v.id || '';
          latestPoleCreatedAt = v.created_at || '';
          break;
        }
      }
      var renderedFp = card.getAttribute('data-bmh-bike-fingerprint') || '';
      var actualFp = approvedAt + '|' + latestPoleId;

      // T-131c — customer-pending-counter detection poistettu (Leon päätös
      // 2026-05-27, counter-flow karsittu). T-111-aikainen
      // `[data-mht-pending-counter]`-elementti on poistettu my-hiisi-bike-
      // card.liquid:sta T-131b:ssä, joten querySelector palauttaisi null
      // joka tapauksessa. formatRelativeTimeSince-helper jää kuolleeksi
      // koodiksi T-132-cleanup:ille — sitä ei kutsuta muualta.

      // Always update the fingerprint attribute so the DOM reflects fresh
      // state. Other JS (or future hydrate runs) can read this without
      // re-fetching.
      if (renderedFp !== actualFp) {
        card.setAttribute('data-bmh-bike-fingerprint', actualFp);
      }

      // ---- Toggle approve-form actions based on fresh approval state ----
      // The approve-form lives inside `[data-mht-approve-actions]` and only
      // renders when Liquid's `mht_is_approved` is false. If approval got
      // set since Liquid rendered, hide the action container so the customer
      // can't double-approve. Find the actions container(s) belonging to
      // this bike's line item — they're inside the versions-timeline render
      // which is nested under the bike-card body.
      var actionContainers = card.querySelectorAll('[data-mht-approve-actions]');
      actionContainers.forEach(function (container) {
        if (approvedAt) {
          // Approved on server → hide local approve form (which may still
          // show stale "Approve this build" button from the Liquid render).
          container.setAttribute('hidden', '');
        }
        // We deliberately do NOT auto-unhide when approval is absent — the
        // open-revision case (approval cleared server-side) requires the
        // customer to refresh for a fresh approve form with current pricing.
      });

      // ---- T-107b: bike-card title resolve (draft→approved→proposed→default) ----
      // Skip when the customer is mid-edit (my-hiisi-rename.js stamps
      // data-bmh-rename-editing="1" on the h3 while the input is open).
      // Overwriting textContent there would discard whatever the user is
      // typing AND break the cancel/restore path which relies on the prior
      // text being preserved in the hidden h3.
      var titleEl = card.querySelector('.bmh-bike-title[data-bmh-rename]');
      if (titleEl && titleEl.dataset.bmhRenameEditing !== '1') {
        var defaultTitle = titleEl.getAttribute('data-bmh-rename-default') || '';
        var freshTitle = resolveBikeCardTitle(lid, drafts, approval, currentProposal, versionsForLid, defaultTitle);
        if (freshTitle && titleEl.textContent !== freshTitle) {
          titleEl.textContent = freshTitle;
          titleEl.setAttribute('aria-label', 'Rename ' + freshTitle);
        }
      }

      // ---- T-107b/T-107c: status pill (pre-approval + approval ladder) ----
      // T-107b shipped the approval-state branch ("Approved {date}"). T-107c
      // extends the resolver to cover every pre-approval state Liquid
      // renders (awaiting_config, configured_pending, consultation_booked,
      // spec_in_preparation, proposal_ready ± is_awaiting). Production-stage
      // ladders (queued/components/cnc/ecoat/assembly/qc/ready) and shipped
      // stay Liquid-rendered — resolveBikeCardPill returns null when it
      // detects production state so the caller skips the patch.
      var pillResolved = resolveBikeCardPill(pillEnv, lid);
      if (pillResolved) {
        var stagePillEl = null;
        var pills = card.querySelectorAll('.bmh-bike-pills .bmh-pill');
        for (var pi = 0; pi < pills.length; pi++) {
          if (!pills[pi].classList.contains('bmh-pill--silver')) {
            stagePillEl = pills[pi];
            break;
          }
        }
        if (stagePillEl) {
          if (stagePillEl.textContent !== pillResolved.text) {
            stagePillEl.textContent = pillResolved.text;
            stagePillEl.setAttribute('aria-label', 'Status: ' + pillResolved.text);
          }
          ['bmh-pill--gold', 'bmh-pill--neutral', 'bmh-pill--green', 'bmh-pill--green-muted'].forEach(function (cls) {
            stagePillEl.classList.remove(cls);
          });
          stagePillEl.classList.add(pillResolved.className);
        }
      }

      // ---- Detect new Pole version and prepend a notice ----
      // `current_proposal[lid]` points at the latest Pole version. If it
      // doesn't match the rendered "current" version card's id, a new Pole
      // version landed since Liquid rendered. Surface a non-blocking notice
      // so the customer knows to refresh.
      var timeline = card.querySelector('[data-mht-timeline]');
      if (!timeline) return;

      // Find the rendered current-pole version id from the big "current" card.
      var currentCard = timeline.querySelector('.mht-nego-card--current[data-mht-version-id]');
      var renderedCurrentId = currentCard ? currentCard.getAttribute('data-mht-version-id') : '';

      // ---- T-107b: version-card h4 label + approval banner ----
      // Patches only fire when approval[lid] is set on the live fetch. If
      // approval is absent we leave the rendered DOM as-is (open_revision
      // case where Liquid rendered the prior "approved" state — the brief
      // calls this out as out-of-scope; customer refreshes to clear).
      if (approvedAt) {
        var approvedVersionIdForH4 = (approval[lid] && approval[lid].version_id) || '';
        if (currentCard && approvedVersionIdForH4 && renderedCurrentId === approvedVersionIdForH4) {
          var h4 = currentCard.querySelector('.mht-nego-vlabel');
          if (h4) {
            var newH4 = approvedVersionIdForH4 + ' — Approved ✓';
            if ((h4.textContent || '').trim() !== newH4) {
              h4.textContent = newH4;
            }
          }
        }
        try {
          ensureApprovalBanner(timeline, approval[lid], invoice1[lid]);
        } catch (e) {
          console.warn('[hiisi-hydrate] approval-banner', e);
        }
      }

      // T-129c — post-approval-revision detection. When approval[lid] is set
      // but Worker's current_proposal[lid] points to a different version
      // than the approved one (Pole used /staff/version after the customer
      // approved), the customer-UI splits into two tiers: the approved card
      // (priority-1 Liquid render) stays on top, and a separate revision-
      // card renders below it. We do TWO things here:
      //   1) If Liquid already rendered the revision-card (data-mht-post-
      //      approval-revision="true" exists in DOM), no banner needed —
      //      the customer can see both cards.
      //   2) If Liquid did NOT render the revision-card (edge-stale render
      //      from before the revision landed), surface a clarifying
      //      notice that explicitly mentions "revision after your approval".
      //      The default T-128b "Header updated" copy would be misleading
      //      because the approved header is correct; only the spec/pricing
      //      below is missing.
      var approvedVersionIdLocal = (approval[lid] && approval[lid].version_id) || '';
      var isPostApprovalRevision =
        !!approvedAt && !!approvedVersionIdLocal && !!freshCurrentId &&
        freshCurrentId !== approvedVersionIdLocal;
      var revisionInDom = currentCard
        ? currentCard.parentNode && currentCard.parentNode.querySelector
          ? currentCard.parentNode.querySelector('[data-mht-post-approval-revision="true"]')
          : null
        : timeline.querySelector('[data-mht-post-approval-revision="true"]');

      if (isPostApprovalRevision) {
        // Liquid already rendered the revision-card in SSR — both halves
        // visible, no banner needed.
        if (revisionInDom) return;
        // Edge-stale Liquid: revision-card missing. Show the T-129c notice
        // unless we already added one this hydrate cycle (back-button etc).
        if (timeline.querySelector('[data-mht-post-approval-revision-notice]')) return;

        var revisionNotice = document.createElement('aside');
        revisionNotice.setAttribute('data-mht-post-approval-revision-notice', '');
        revisionNotice.setAttribute('role', 'status');
        revisionNotice.setAttribute('aria-live', 'polite');
        revisionNotice.className = 'mht-fresh-update-notice mht-fresh-update-notice--revision';
        revisionNotice.style.padding = '12px 16px';
        revisionNotice.style.marginBottom = '16px';
        revisionNotice.style.background = 'rgba(200, 168, 110, 0.12)';
        revisionNotice.style.border = '1px solid rgba(200, 168, 110, 0.45)';
        revisionNotice.style.borderRadius = '8px';
        revisionNotice.style.color = '#e8e6e1';
        revisionNotice.style.fontSize = '15px';
        revisionNotice.style.display = 'flex';
        revisionNotice.style.flexWrap = 'wrap';
        revisionNotice.style.gap = '12px';
        revisionNotice.style.alignItems = 'center';
        revisionNotice.style.justifyContent = 'space-between';

        var revisionMsg = document.createElement('span');
        revisionMsg.textContent = '📩 Pole sent a revision (' + freshCurrentId +
          ') after your approval of ' + approvedVersionIdLocal +
          '. Refresh to review the revision alongside your approved build.';
        revisionNotice.appendChild(revisionMsg);

        var revisionBtn = document.createElement('button');
        revisionBtn.type = 'button';
        revisionBtn.textContent = 'Refresh';
        revisionBtn.className = 'mht-fresh-update-notice__refresh';
        revisionBtn.style.padding = '8px 18px';
        revisionBtn.style.background = '#c8a86e';
        revisionBtn.style.color = '#0a0a0a';
        revisionBtn.style.border = '0';
        revisionBtn.style.borderRadius = '999px';
        revisionBtn.style.cursor = 'pointer';
        revisionBtn.style.fontSize = '14px';
        revisionBtn.style.fontWeight = '600';
        revisionBtn.addEventListener('click', function () {
          try { sessionStorage.removeItem('bmh-hydrate-attempted-reload'); } catch (_e) { /* ignore */ }
          // T-128d cache-bust pattern — beats Shopify edge's stale Liquid.
          try {
            var refreshUrl = new URL(window.location.href);
            refreshUrl.searchParams.set('_t', String(Date.now()));
            window.location.replace(refreshUrl.toString());
          } catch (_err) {
            window.location.reload();
          }
        });
        revisionNotice.appendChild(revisionBtn);

        timeline.insertBefore(revisionNotice, timeline.firstChild);
        return;
      }

      if (freshCurrentId && renderedCurrentId && freshCurrentId !== renderedCurrentId) {
        // T-128b — patch the current-card header (h4 + meta + data-attribute)
        // from Worker data so the customer sees the new version id without
        // reloading. Spec-dl and pricing intentionally stay Liquid-rendered;
        // duplicating the 550-line spec/pricing snippets in JS would risk
        // cost-leak slips + display mismatches. The fresh-update-notice
        // (below) makes the spec-dl-stale state explicit so the customer
        // knows to click Refresh (or wait for the next focus event, T-128d)
        // to see full spec/pricing. T-110's "no DOM construction" principle
        // is relaxed only here: in-place text patches to existing nodes,
        // no element creation.
        if (currentCard && !approvedAt) {
          var freshEntry = null;
          var versionsForCard = versions[lid];
          if (Array.isArray(versionsForCard)) {
            for (var vi = versionsForCard.length - 1; vi >= 0; vi--) {
              var entryCandidate = versionsForCard[vi];
              if (entryCandidate && entryCandidate.id === freshCurrentId && entryCandidate.type === 'pole') {
                freshEntry = entryCandidate;
                break;
              }
            }
          }
          if (freshEntry) {
            var freshH4 = currentCard.querySelector('.mht-nego-vlabel');
            if (freshH4) {
              var newFreshH4 = "Pole's latest build — " + freshCurrentId;
              if ((freshH4.textContent || '').trim() !== newFreshH4) {
                freshH4.textContent = newFreshH4;
              }
            }
            var freshMeta = currentCard.querySelector('.mht-nego-meta');
            if (freshMeta) {
              // Mirror Liquid's `Sent {{ v.created_at | date: '%-d %b %Y %H:%M' }}
              // · {{ v.author_name | default: 'Pole staff' }}` format. We use
              // formatMetaDate (defined below) which produces matching output
              // for the customer locale.
              var freshAuthor = (freshEntry.author_name && String(freshEntry.author_name).trim()) || 'Pole staff';
              var freshDateStr = formatMetaDate(freshEntry.created_at);
              freshMeta.textContent = 'Sent ' + freshDateStr + ' · ' + freshAuthor;
            }
            currentCard.setAttribute('data-mht-version-id', freshCurrentId);
          }
        }

        // Don't double-prepend if a previous hydrate run already showed the
        // notice (e.g. customer dismissed an interim hydrate and another
        // ran).
        if (timeline.querySelector('[data-mht-fresh-update-notice]')) return;

        var notice = document.createElement('aside');
        notice.setAttribute('data-mht-fresh-update-notice', '');
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        notice.className = 'mht-fresh-update-notice';
        notice.style.padding = '12px 16px';
        notice.style.marginBottom = '16px';
        notice.style.background = 'rgba(200, 168, 110, 0.12)';
        notice.style.border = '1px solid rgba(200, 168, 110, 0.45)';
        notice.style.borderRadius = '8px';
        notice.style.color = '#e8e6e1';
        notice.style.fontSize = '15px';
        notice.style.display = 'flex';
        notice.style.flexWrap = 'wrap';
        notice.style.gap = '12px';
        notice.style.alignItems = 'center';
        notice.style.justifyContent = 'space-between';

        var msg = document.createElement('span');
        // T-128b — clearer copy: header is now correct, but spec/pricing
        // below still reflect the older render. Tell the customer that
        // explicitly so the Refresh CTA's purpose is obvious.
        msg.textContent = '📩 Pole sent a new version (' + freshCurrentId + '). Header updated; refresh to load the new spec and pricing.';
        notice.appendChild(msg);

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Refresh';
        btn.className = 'mht-fresh-update-notice__refresh';
        btn.style.padding = '8px 18px';
        btn.style.background = '#c8a86e';
        btn.style.color = '#0a0a0a';
        btn.style.border = '0';
        btn.style.borderRadius = '999px';
        btn.style.cursor = 'pointer';
        btn.style.fontSize = '14px';
        btn.style.fontWeight = '600';
        btn.addEventListener('click', function () {
          // T-121 — defensively clear the historical T-108-fix sessionStorage
          // flag (`bmh-hydrate-attempted-reload`) before navigating. T-110
          // removed the auto-reload loop and stopped writing the flag, but
          // a customer whose browser still carries one from a pre-T-110
          // session would otherwise have any future auto-reload (if it's
          // ever reintroduced) silently skipped on this tab. The flag is
          // scoped to the auto-reload loop-cap; a customer-initiated refresh
          // should never inherit it. Wrapped in try/catch because
          // sessionStorage throws in private-mode / disabled-storage tabs.
          try { sessionStorage.removeItem('bmh-hydrate-attempted-reload'); } catch (_e) { /* ignore */ }
          // T-128d — cache-bust via `?_t=<ts>` so Shopify's edge can't return
          // the same stale Liquid snapshot. Pre-T-128d the button used a bare
          // `window.location.reload()`, which let the edge serve the cached
          // render and made the button feel unresponsive ("clicked Refresh,
          // nothing changed"). Mirrors sections/bespoke-my-hiisi.liquid's
          // reloadFresh() pattern.
          try {
            var refreshUrl = new URL(window.location.href);
            refreshUrl.searchParams.set('_t', String(Date.now()));
            window.location.replace(refreshUrl.toString());
          } catch (_err) {
            window.location.reload();
          }
        });
        notice.appendChild(btn);

        timeline.insertBefore(notice, timeline.firstChild);
      }
    });
  }

  // T-128b — format a VersionEntry.created_at into the same shape Liquid
  // uses in mht-nego-meta: "%-d %b %Y %H:%M" → "30 May 2026 04:31". Locale
  // is forced to en-GB because the rest of the page renders English regardless
  // of customer locale (Liquid filter pipeline is en-only post-T-094).
  function formatMetaDate(iso) {
    if (typeof iso !== 'string' || !iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var day = d.getUTCDate();
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var month = months[d.getUTCMonth()];
    var year = d.getUTCFullYear();
    var hh = String(d.getUTCHours()).padStart(2, '0');
    var mm = String(d.getUTCMinutes()).padStart(2, '0');
    return day + ' ' + month + ' ' + year + ' ' + hh + ':' + mm;
  }

  // ---------------------------------------------------------------------------
  // applyOrderSummary — recompute bottom-line totals from fresh versions/drafts
  // ---------------------------------------------------------------------------
  // T-112: the Liquid render reads spec.totals via a versions-then-drafts
  // priority chain (my-hiisi-order-total.liquid). The cache-bust hydrate
  // re-runs the same chain client-side against the fresh /customer/order
  // response and rewrites the bottom-line amounts (subtotal / VAT / total
  // incl. VAT / balance / Invoice 1 / Invoice 2 / pending notice).
  //
  // Scope intentionally narrow:
  //   - Per-bike row text (Bike N · frame summary · build type · €X) is NOT
  //     re-rendered. The Liquid render carries the original price; if hydrate
  //     finds new totals, the bottom-line is corrected but the per-bike row
  //     might still read "Configure to see total" until the next page-load.
  //     Reconstructing the row label requires frame-color / wheel / size
  //     mapping that's already in Liquid — duplicating it in JS would drift
  //     fast. Acceptable: customer sees correct subtotal/balance/invoices
  //     immediately; row labels self-correct on next refresh.
  //   - VAT factor (per-mille) and deposit display value are read from the
  //     grid's data-* attributes so we don't recompute the EU/non-EU split
  //     or the per-country VAT rate client-side.
  //
  // Cost-leak boundary (T-039 / T-103d): reads only versions / current_
  // proposal / drafts from the /customer/order body, all customer-safe.
  // T-148 — derive the customer-facing line figures from one spec.totals.
  // priceEur is the pre-discount build price; servicesEur / discountEur / netEur
  // are stamped by the Worker (buildTotals) on Pole versions. Pre-T-148 specs
  // lack the extras → services/discount default 0 and net falls back to price,
  // so the recompute matches the pre-T-148 behaviour byte-for-byte.
  function totalsFromSpec(spec) {
    var t = (spec && spec.totals) || {};
    var price = typeof t.priceEur === 'number' && t.priceEur > 0 ? t.priceEur : 0;
    if (!price) return null;
    var services = typeof t.servicesEur === 'number' && t.servicesEur > 0 ? t.servicesEur : 0;
    var discount = typeof t.discountEur === 'number' && t.discountEur > 0 ? t.discountEur : 0;
    var net = typeof t.netEur === 'number' && t.netEur >= 0 ? t.netEur : price;
    return { price: price, services: services, discount: discount, net: net };
  }

  // Returns { price, services, discount, net } for a line item via the same
  // versions-then-drafts priority chain the Liquid uses, or null when pending.
  function resolvePerLidTotals(lid, versions, currentProposal, drafts) {
    // Priority 1: versions[lid] entry with id = current_proposal[lid].
    var currentVid = (currentProposal && currentProposal[lid]) || null;
    if (currentVid) {
      var lidVersions = (versions && versions[lid]) || [];
      for (var i = 0; i < lidVersions.length; i++) {
        var v = lidVersions[i];
        if (v && v.id === currentVid) {
          var fromVersion = totalsFromSpec(v.spec);
          if (fromVersion) return fromVersion;
          break;
        }
      }
    }
    // Priority 2: drafts[lid].spec (customer's last save — never discounted).
    var draftEntry = drafts && drafts[lid];
    var fromDraft = draftEntry && totalsFromSpec(draftEntry.spec);
    if (fromDraft) return fromDraft;
    // No priced spec yet — counts toward pending.
    return null;
  }

  function setOrderSummaryAmount(grid, key, eurInt) {
    var spans = grid.querySelectorAll('[data-bmh-os-amount="' + key + '"]');
    spans.forEach(function (span) {
      span.setAttribute('data-hiisi-eur', String(eurInt));
      // Reset hydration marker so HiisiCurrency.hydrate re-formats the text.
      span.removeAttribute('data-hiisi-hydrated');
      // Mirror the Liquid output format: "€{integer with thousands separator}".
      // toLocaleString gives us locale-appropriate grouping; we hard-code
      // 'en-US' to match the Liquid `money_without_currency` pattern (Liquid
      // uses Shopify's shop locale but we render English-style across the
      // theme).
      try {
        span.textContent = '€' + Number(eurInt).toLocaleString('en-US');
      } catch (_e) {
        span.textContent = '€' + String(eurInt);
      }
    });
    var locals = grid.querySelectorAll('[data-bmh-os-amount-local="' + key + '"]');
    locals.forEach(function (span) {
      span.setAttribute('data-hiisi-eur-local', String(eurInt));
      span.removeAttribute('data-hiisi-hydrated');
      // The local-estimate cell is populated by HiisiCurrency.hydrate; clear
      // the stale text so the cell shows nothing until re-hydration paints
      // the new "≈local" estimate. (Hydrate runs at end of applyOrderSummary.)
      span.textContent = '';
    });
  }

  // Pending notice — always-rendered with `hidden` toggle (T-112 snippet
  // change). Show/hide + update count + plural copy.
  function updateOrderSummaryPending(grid, pendingCount) {
    var notice = grid.querySelector('[data-bmh-os-pending-notice]');
    if (!notice) return;
    if (pendingCount > 0) {
      notice.removeAttribute('hidden');
      var countEl = notice.querySelector('[data-bmh-os-pending-count]');
      if (countEl) countEl.textContent = String(pendingCount);
      var nounEl = notice.querySelector('[data-bmh-os-pending-noun]');
      if (nounEl) nounEl.textContent = pendingCount === 1 ? 'bike' : 'bikes';
      var verbEl = notice.querySelector('[data-bmh-os-pending-verb]');
      if (verbEl) verbEl.textContent = pendingCount === 1 ? 'needs' : 'need';
    } else {
      notice.setAttribute('hidden', '');
    }
  }

  function applyOrderSummary(versions, currentProposal, drafts) {
    var grid = document.querySelector('[data-bmh-os-grid]');
    if (!grid) return;

    versions = versions || {};
    currentProposal = currentProposal || {};
    drafts = drafts || {};

    // Read the per-render constants Liquid stamped onto the grid.
    var vatFactorPm = parseInt(grid.getAttribute('data-bmh-os-vat-factor-pm') || '1000', 10);
    if (!Number.isFinite(vatFactorPm) || vatFactorPm < 1000) vatFactorPm = 1000;
    var isEu = grid.getAttribute('data-bmh-os-is-eu') === 'true';
    var bikeCount = parseInt(grid.getAttribute('data-bmh-os-bike-count') || '0', 10);
    if (!Number.isFinite(bikeCount) || bikeCount < 0) bikeCount = 0;
    // T-136c NET-VAT: the ex-VAT deposit value, shown in the "Less deposit
    // (VAT 0%)" row in all markets and subtracted before VAT (replaces the old
    // EU/non-EU deposit-display split). Server stamps it on the grid.
    var depositNet = parseInt(grid.getAttribute('data-bmh-os-deposit-net') || '0', 10);
    if (!Number.isFinite(depositNet) || depositNet < 0) depositNet = 0;
    // 2026-08-29 — tarjouksesta syntyneella tilauksella rahasummat tulevat
    // hyvaksytysta tarjousdokumentista, jota tama uudelleenlaskenta ei tunne:
    // se ei tieda rahdista eika maksuerien maarasta, joten se kirjoitti
    // palvelimen luvut yli ja sivulla oli kaksi eri saldoa ja puolikas
    // "koko saldo" -lasku. Naissa palvelimen render on ainoa totuus; vain
    // odottavien pyorien huomautus paivitetaan.
    var fromQuote = grid.getAttribute('data-bmh-os-from-quote') === 'true';
    // Kertamaksu: koko saldo yhdella laskulla, sama saanto kuin Liquidissa.
    var singlePayment = grid.getAttribute('data-bmh-os-single-payment') === 'true';

    // Walk the rendered bike rows to recover their line_item_ids. We don't
    // require per-row data-li-id hooks (the Liquid emits a flat CSS grid of
    // cells, not row-scoped wrappers) — instead, iterate the order's line
    // items via the keys present in versions ∪ drafts. The Liquid loop
    // walks order.line_items in the same order, so the per-bike subtotal
    // accumulation matches whatever Liquid would have produced.
    //
    // Pending count: every lid whose resolved price is 0 is pending. This is
    // the same gate Liquid applies (ot_price > 0 → subtotal else pending++).
    var allLids = {};
    Object.keys(versions).forEach(function (k) { allLids[k] = true; });
    Object.keys(drafts).forEach(function (k) { allLids[k] = true; });
    var subtotal = 0;
    var servicesTotal = 0;
    var discountTotal = 0;
    var netTotal = 0;
    var pendingCount = 0;
    var seenLids = 0;
    Object.keys(allLids).forEach(function (lid) {
      var t = resolvePerLidTotals(lid, versions, currentProposal, drafts);
      seenLids += 1;
      if (t) {
        subtotal += t.price;
        servicesTotal += t.services;
        discountTotal += t.discount;
        netTotal += t.net;
      } else {
        pendingCount += 1;
      }
    });
    // Any line items present in the order but missing from versions ∪ drafts
    // also count as pending (matches Liquid: missing draft entry → spec is
    // nil → price is 0 → pending++). Use bikeCount from the grid attr as the
    // authoritative count.
    if (bikeCount > seenLids) {
      pendingCount += bikeCount - seenLids;
    }

    // Liquid math mirror (T-136c NET-VAT) — keep IDENTICAL to
    // my-hiisi-order-total.liquid. VAT is charged on the net (subtotal minus
    // the ex-VAT deposit), not on the gross subtotal. Liquid's integer divide
    // floors after +500 half-up, so Math.floor (not Math.round) matches the
    // server byte-for-byte.
    // T-148 — VAT + deposit apply to netTotal (Σ netEur = subtotal + services −
    // discount), matching my-hiisi-order-total.liquid. For pre-T-148 specs
    // netTotal === subtotal so this is unchanged.
    var netExVat = netTotal - depositNet;
    if (netExVat < 0) netExVat = 0;
    var vatBasisPm = vatFactorPm - 1000;
    var vatAmount = Math.floor((netExVat * vatBasisPm + 500) / 1000);
    var balanceDue = netExVat + vatAmount;
    if (balanceDue < 0) balanceDue = 0;
    var invoice1 = singlePayment ? balanceDue : Math.floor(balanceDue / 2);
    var invoice2 = singlePayment ? 0 : balanceDue - invoice1;

    // Update each amount span. The deposit-net row exists in all markets; the
    // net + vat rows only exist for EU markets (querySelectorAll returns 0
    // nodes on non-EU pages → setter is a no-op, no errors thrown).
    if (fromQuote) {
      updateOrderSummaryPending(grid, pendingCount);
      return;
    }

    setOrderSummaryAmount(grid, 'subtotal', subtotal);
    // T-148 — services / discount spans only exist when the server rendered the
    // row (total > 0). setOrderSummaryAmount is a no-op when the span is absent,
    // so a fresh discount that appeared after render is handled by the
    // fingerprint reloadFresh path (a new version id forces a full reload),
    // never by a half-updated summary.
    setOrderSummaryAmount(grid, 'services', servicesTotal);
    setOrderSummaryAmount(grid, 'discount', discountTotal);
    setOrderSummaryAmount(grid, 'deposit-net', depositNet);
    if (isEu) {
      setOrderSummaryAmount(grid, 'net', netExVat);
      setOrderSummaryAmount(grid, 'vat', vatAmount);
    }
    setOrderSummaryAmount(grid, 'balance', balanceDue);
    setOrderSummaryAmount(grid, 'invoice1', invoice1);
    setOrderSummaryAmount(grid, 'invoice2', invoice2);

    updateOrderSummaryPending(grid, pendingCount);

    // Re-run HiisiCurrency.hydrate so the (now-fresh) data-hiisi-eur values
    // get formatted with thousands separators AND the local-estimate spans
    // get re-populated. HiisiCurrency uses the data-hiisi-hydrated marker
    // we cleared above to know which nodes to revisit.
    if (window.HiisiCurrency && typeof window.HiisiCurrency.hydrate === 'function') {
      try { window.HiisiCurrency.hydrate(document); } catch (_e) { /* ignore */ }
    }
  }

  // ---------------------------------------------------------------------------
  // applyJourney — derived-state journey-card patch
  // ---------------------------------------------------------------------------
  // T-113: mirror the journey-stepper.liquid derived state machine on the
  // client so a fresh /customer/order fetch can correct the rendered step
  // states + progress hint in place. Same "later implies earlier done"
  // semantic — see snippets/my-hiisi-journey-stepper.liquid for the Liquid
  // version of this logic.
  //
  // Patches:
  //   - per-step `bmh-journey__step--{state}` class on each
  //     `[data-bmh-journey-step]` li + matching `data-bmh-journey-state`
  //     attr
  //   - per-step `[data-bmh-journey-detail]` `hidden` toggle (text content
  //     itself stays Liquid-rendered — we just show/hide based on state)
  //   - progress headline `[data-bmh-journey-progress-step]` (1-5) +
  //     `[data-bmh-journey-progress-hint]` text
  //   - locked-card meta mode flip (post-approval → "Last updated …")
  //
  // Not re-rendered: step labels (static), markers (CSS-driven), detail
  // text (Liquid owns the strings; JS would have to recompute dates +
  // counts + currency amounts which is bigger scope than the brief).
  //
  // Cost-leak: reads only versions / approval / drafts / current_proposal
  // / production from the customer/order body. No hiisi_pole_internal.
  function progressHintForCurrent(current) {
    switch (current) {
      case 'configure':    return 'configure your bike';
      case 'consultation': return 'book your fitting call';
      case 'approve':      return 'review and approve your build';
      case 'invoice1':     return 'pay Invoice 1 to start production';
      case 'production':   return 'production starts soon';
      case 'done':         return 'your bikes are on the way';
      default:             return '';
    }
  }

  function applyJourney(versions, approval, drafts, currentProposal, production, consultations, consultationAt, invoiceScalars, customerSteps) {
    var card = document.querySelector('[data-bmh-journey-card]');
    if (!card) return;

    // 2026-08-10 — consultation switched off for this order: Step 3 is not
    // part of the journey at all. Liquid already drops the li, but a stale
    // edge render can still carry it, so hide it here too and count the
    // journey out of the steps that actually exist.
    var journeySteps = resolveCustomerSteps(customerSteps);
    var consultEnabled = journeySteps.consultation;
    var consultLi = card.querySelector('[data-bmh-journey-step="3"]');
    if (!consultEnabled && consultLi) {
      consultLi.setAttribute('hidden', '');
      var journeyList = consultLi.parentNode;
      if (journeyList && journeyList.classList) journeyList.classList.add('bmh-journey--5');
    }

    versions = versions || {};
    approval = approval || {};
    drafts = drafts || {};
    currentProposal = currentProposal || {};
    production = production || {};
    consultations = consultations && typeof consultations === 'object' ? consultations : {};
    invoiceScalars = invoiceScalars && typeof invoiceScalars === 'object' ? invoiceScalars : {};

    // ---- Collect predicate counts ----
    var allLids = {};
    Object.keys(versions).forEach(function (k) { allLids[k] = true; });
    Object.keys(drafts).forEach(function (k) { allLids[k] = true; });
    Object.keys(approval).forEach(function (k) { allLids[k] = true; });
    Object.keys(production).forEach(function (k) { allLids[k] = true; });
    var lids = Object.keys(allLids);
    var total = lids.length;

    var draftsDone = 0;
    var approvedDone = 0;
    var shippedDone = 0;
    lids.forEach(function (lid) {
      var draft = drafts[lid];
      if (draft && draft.spec) draftsDone += 1;
      var ap = approval[lid];
      if (ap && ap.approved_at) approvedDone += 1;
      var prod = production[lid];
      if (prod && prod.shipped_at) shippedDone += 1;
    });

    // T-127b — derive consultation state from Worker /customer/order data
    // (consultations map + consultation_at scalar), NOT the rendered Step 3
    // DOM. Pre-T-127b read [data-bmh-journey-step="3"]'s state + text content,
    // which meant a stale edge-cached Liquid snapshot pinned the journey to
    // whatever was true at SSR-time even when staff had since flipped Done.
    // kts. proposals/handout-T-127-state-resolve-juurisyy.md §Juuri 2.
    //
    // Semantics: any consultations[<lid>].done_at OR consultation_at < now
    // → past. consultation_at > now → future. Per-bike done supersedes scalar
    // direction (T-127a fills the scalar one-way when staff toggles Done, but
    // the map's done_at can land first if Worker's metafieldsSet partially
    // succeeded or the scalar fill was skipped because it was already future).
    var consultDoneInPast = false;
    var consultBookedFuture = false;
    var consultKeys = Object.keys(consultations);
    for (var ci = 0; ci < consultKeys.length; ci++) {
      var consultEntry = consultations[consultKeys[ci]];
      if (consultEntry && typeof consultEntry === 'object' && typeof consultEntry.done_at === 'string' && consultEntry.done_at) {
        consultDoneInPast = true;
        break;
      }
    }
    if (!consultDoneInPast && typeof consultationAt === 'string' && consultationAt) {
      var consultUnix = Date.parse(consultationAt);
      if (!isNaN(consultUnix)) {
        if (consultUnix < Date.now()) consultDoneInPast = true;
        else if (consultUnix > Date.now()) consultBookedFuture = true;
      }
    }
    // Consultation switched off → no later step may hang on consultation_at.
    // Mirrors the same reset in my-hiisi-journey-stepper.liquid. The stored
    // timestamps are left alone; they simply stop driving the journey.
    if (!consultEnabled) {
      consultDoneInPast = false;
      consultBookedFuture = false;
    }
    var consultSet = consultDoneInPast || consultBookedFuture;

    // ---- Derive js_current + per-step states (mirrors Liquid) ----
    var allShipped = total > 0 && shippedDone >= total;
    var approvalSet = total > 0 && approvedDone >= total;
    var draftsDoneAll = total > 0 && draftsDone >= total;
    // T-107e — Invoice 1 paid is its own step (5). Order-scalar gate.
    var invoice1PaidSet = !!invoiceScalars.invoice1_paid_at;

    var current = 'configure';
    if (allShipped) current = 'done';
    else if (invoice1PaidSet) current = 'production';
    else if (approvalSet) current = 'invoice1';
    else if (consultSet) current = 'approve';
    // Without a Consultation step the customer's attention goes straight from
    // "configured" to "approve your build".
    else if (draftsDoneAll) current = consultEnabled ? 'consultation' : 'approve';

    var s1 = 'done';
    var s2 = 'upcoming';
    if (draftsDoneAll || consultSet || approvalSet || allShipped) s2 = 'done';
    else if (current === 'configure') s2 = 'current';
    var s3 = 'upcoming';
    if (consultDoneInPast || consultBookedFuture || approvalSet || allShipped) s3 = 'done';
    else if (current === 'consultation') s3 = 'current';
    var s4 = 'upcoming';
    if (approvalSet || invoice1PaidSet || allShipped) s4 = 'done';
    else if (current === 'approve') s4 = 'current';
    // Step 5 — Invoice 1 paid.
    var s5 = 'upcoming';
    if (invoice1PaidSet || allShipped) s5 = 'done';
    else if (current === 'invoice1') s5 = 'current';
    // Step 6 — Production & ship.
    var s6 = 'upcoming';
    if (allShipped) s6 = 'done';
    else if (current === 'production') s6 = 'current';

    var stepStates = [s1, s2, s3, s4, s5, s6];
    // Count only the steps that exist. With consultation off the journey is
    // five steps long, so "Step 6 of 6" would be unreachable and the headline
    // would stall one short of the end.
    var stepTotal = consultEnabled ? 6 : 5;
    var doneCount = 0;
    for (var i = 0; i < stepStates.length; i++) {
      if (!consultEnabled && i === 2) continue; // index 2 === step 3, Consultation
      if (stepStates[i] === 'done') doneCount += 1;
    }
    var progressStep = Math.min(stepTotal, doneCount + 1);

    // ---- Patch per-step classes + detail visibility ----
    for (var n = 1; n <= 6; n++) {
      var li = card.querySelector('[data-bmh-journey-step="' + n + '"]');
      if (!li) continue;
      var newState = stepStates[n - 1];
      var prevState = li.getAttribute('data-bmh-journey-state');
      if (prevState !== newState) {
        li.classList.remove('bmh-journey__step--done', 'bmh-journey__step--current', 'bmh-journey__step--upcoming');
        li.classList.add('bmh-journey__step--' + newState);
        li.setAttribute('data-bmh-journey-state', newState);
      }
      var detail = li.querySelector('[data-bmh-journey-detail="' + n + '"]');
      if (detail) {
        var detailText = (detail.textContent || '').trim();
        var hideDetail = detailText === '' || ((n === 2 || n === 5) && newState === 'upcoming');
        if (hideDetail) detail.setAttribute('hidden', '');
        else detail.removeAttribute('hidden');
      }
    }

    // ---- Patch progress headline ----
    var progStepEl = card.querySelector('[data-bmh-journey-progress-step]');
    if (progStepEl) progStepEl.textContent = String(progressStep);
    var progTotalEl = card.querySelector('[data-bmh-journey-progress-total]');
    if (progTotalEl) progTotalEl.textContent = String(stepTotal);
    card.setAttribute('data-bmh-journey-consultation', consultEnabled ? 'on' : 'off');
    var progHintEl = card.querySelector('[data-bmh-journey-progress-hint]');
    if (progHintEl) {
      var newHint = progressHintForCurrent(current);
      progHintEl.textContent = newHint;
      if (newHint) progHintEl.removeAttribute('hidden');
      else progHintEl.setAttribute('hidden', '');
    }

    // ---- Locked-card meta mode (consultation-widget) ----
    // When approval lands since Liquid render, flip the per-bike meta from
    // "pre" → "post". The text-content swap is intentionally NOT done here
    // (different Liquid branches with different structure); we only flip
    // the `data-bmh-locked-meta-mode` attribute so future CSS or a follow-
    // up PR could style differently. The wording mismatch in the brief's
    // window between hydrate and next page-load is mild.
    if (approvalSet || consultSet) {
      document.querySelectorAll('[data-bmh-locked-meta]').forEach(function (meta) {
        meta.setAttribute('data-bmh-locked-meta-mode', 'post');
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Orchestrator
  // ---------------------------------------------------------------------------
  ready(function () {
    var detail = resolveActiveDetail();
    if (!detail) return;
    var orderId = detail.getAttribute('data-order-id');
    if (!orderId) return;

    var url = '/apps/hiisi-bespoke/customer/order?order=' + encodeURIComponent(orderId);

    fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) return null;
      return res.json();
    }).then(function (body) {
      if (!body || body.ok !== true) return;
      // Maksutapa ennen muita: banneritekstit lukevat sita.
      if (body.payment_plan === 'single') hiisiSinglePayment = true;
      else if (body.payment_plan === 'instalments') hiisiSinglePayment = false;
      // Apply each section in isolation — a bug in one apply* function must
      // not poison the others.
      try { applyConsultation(body.profiles, body.consultations, body.consultation_at, body.customer_steps); } catch (e) { console.warn('[hiisi-hydrate] consultation', e); }
      try { applyTerms(body.terms); } catch (e) { console.warn('[hiisi-hydrate] terms', e); }
      try { applyInvoices(body.invoice_scalars, body.approval); } catch (e) { console.warn('[hiisi-hydrate] invoices', e); }
      try { applyBikeCards(body.versions, body.approval, body.current_proposal, body.drafts, body.invoice1, body.production, body.consultations, body.consultation_at); } catch (e) { console.warn('[hiisi-hydrate] bike-cards', e); }
      try { applyOrderSummary(body.versions, body.current_proposal, body.drafts); } catch (e) { console.warn('[hiisi-hydrate] order-summary', e); }
      try { applyJourney(body.versions, body.approval, body.drafts, body.current_proposal, body.production, body.consultations, body.consultation_at, body.invoice_scalars, body.customer_steps); } catch (e) { console.warn('[hiisi-hydrate] journey', e); }
    }).catch(function (e) {
      // Network / parse errors: silently leave Liquid render visible.
      console.warn('[hiisi-hydrate] fetch failed', e);
    });
    readSinglePaymentFromDom();

    // T-129c — Approve-revision click handler. Wired once at ready; the
    // listener delegates on click events so revision-cards rendered by
    // hydrate (future-proof) or Liquid SSR both work without per-card
    // wiring. Single-shot confirm flow: window.confirm() gates the POST,
    // payment_method defaults to 'invoice' (Tier-1 — modal-flow for
    // payment/terms selection is T-130 scope). terms_version is the
    // current terms baseline from the Liquid render's data-attr.
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || target.nodeType !== 1) return;
      var btn = target.closest ? target.closest('[data-mht-action="approve-revision"]') : null;
      if (!btn) return;
      e.preventDefault();

      var versionId = btn.getAttribute('data-mht-revision-version-id') || '';
      var lineItemId = btn.getAttribute('data-mht-line-item-id') || '';
      var clickedOrderId = btn.getAttribute('data-mht-order-id') || '';
      if (!versionId || !lineItemId || !clickedOrderId) {
        console.warn('[hiisi-hydrate] approve-revision missing data attributes');
        return;
      }

      // terms_version inherits from the existing approval card (the
      // version the customer originally approved); customer doesn't need
      // to re-tick terms unless the terms_version on file rolled forward.
      // Worker accepts the value as-is. Pre-fill with 'v1.0' if no data-
      // attribute is present (matches the legacy approve-form default in
      // versions-timeline.liquid).
      var termsVersion = btn.getAttribute('data-mht-terms-version') || 'v1.0';

      var confirmMsg =
        'Approve revision ' + versionId + '? Your previous approval will move ' +
        'to your order history. Pole will then re-issue Invoice 1 against the ' +
        'revised build.';
      // eslint-disable-next-line no-alert
      if (!window.confirm(confirmMsg)) return;

      btn.disabled = true;
      btn.textContent = 'Approving…';

      fetch('/apps/hiisi-bespoke/customer/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          order_id: clickedOrderId,
          line_item_id: lineItemId,
          version_id: versionId,
          terms_version: termsVersion,
          payment_method: 'invoice',
        }),
      }).then(function (res) {
        if (!res.ok) {
          return res.text().then(function (txt) {
            throw new Error('approve failed (HTTP ' + res.status + '): ' + txt.slice(0, 200));
          });
        }
        // Reload with cache-bust so Shopify edge re-renders Liquid against
        // the new approval state. Mirrors sections/bespoke-my-hiisi.liquid
        // reloadFresh().
        try {
          var refreshUrl = new URL(window.location.href);
          refreshUrl.searchParams.set('_t', String(Date.now()));
          window.location.replace(refreshUrl.toString());
        } catch (_err) {
          window.location.reload();
        }
      }).catch(function (err) {
        console.warn('[hiisi-hydrate] approve-revision failed', err);
        btn.disabled = false;
        btn.textContent = 'Approve revision ' + versionId;
        // eslint-disable-next-line no-alert
        window.alert(
          'Approval did not go through. Please refresh and try again — if the ' +
          'problem persists, contact Pole.',
        );
      });
    });
  });
})();
